import React, { useEffect, useCallback } from "react";
import { format } from "date-fns";
import { CSVLink } from "react-csv";
import FuzzySearch from "fuzzy-search";
import { withStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Label from "../label/Label";
import SingleLineTextInput from "../../components/singleLineTextInput/SingleLineTextInput";
import Button from "../../components/button/Button";
import DropdownSelect from "../../components/dropdownSelect/DropdownSelect";

const styles = (theme) => ({
  tableHeader: {
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tableWrapper: {
    width: "100%",
    overflowY: "auto",
  },
  table: {
    width: "100%",
    borderSpacing: "0px",
  },
  tableHeading: {
    color: "#7B7B7B",
    borderBottom: "1px solid #D7D7D7",
  },
  tableCell: {
    borderBottom: "1px solid #D7D7D7",
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 13,
  },
  tableActionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > div": {
      marginLeft: 5,
    },
  },
  yesValue: {
    backgroundColor: "#00823E",
    width: 100,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  noValue: {
    backgroundColor: "lightgrey",
    width: 100,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  filterSection: {
    marginBottom: 20,
    display: "flex",
  },
  filterSelects: {
    display: "flex",
  },
  filterSelect: {
    width: 150,
    marginRight: 10,
  },
  fieldTag: {
    backgroundColor: "#000",
    padding: "5px 20px",
    color: "#fff",
    textAlign: "center",
    marginLeft: 10,
  },
});

function renderTableHeadings({ classes, headings }) {
  if (headings) {
    return headings.map((heading) => {
      if (typeof heading.label === "string") {
        return (
          <TableCell
            key={"heading-" + heading.label}
            className={classes.tableHeading}
          >
            <Label title={heading.label} />
          </TableCell>
        );
      }
      return false;
    });
  }
  return false;
}

function getIcon(type) {
  switch (type) {
    case "edit":
      return <EditIcon />;

    case "delete":
      return <DeleteIcon />;

    case "email":
      return <EmailIcon />;

    case "pay":
      return <AccountBalanceWalletIcon />;

    case "fee-breakdown":
      return <ReceiptIcon />;

    default:
      return <EditIcon />;
  }
}

function renderTableActions({ rowIndex, dataItem, rowActions }) {
  return (
    rowActions &&
    rowActions
      .filter(
        (rowAction) =>
          rowAction.onClick &&
          !rowAction.hideButton(dataItem.data) &&
          (!rowAction.hidden || !rowAction.hidden(dataItem.data))
      )
      .map((rowAction) => {
        const rowActionContent = (
          <div key={`action-${rowIndex}-${rowAction.type}`}>
            <IconButton onClick={() => rowAction.onClick(dataItem.data)}>
              {getIcon(rowAction.type)}
            </IconButton>
          </div>
        );

        if (rowAction.tooltip) {
          return (
            <Tooltip title={rowAction.tooltip}>{rowActionContent}</Tooltip>
          );
        }
        return rowActionContent;
      })
  );
}

function filterData({ searchTerm, data, headings }) {
  let keys = [];
  for (let i = 0; i < headings.length; i++) {
    const heading = headings[i];

    if (heading.key && !heading.subKey) {
      keys.push(heading.key);
    } else if (heading.key && heading.subKey) {
      keys.push(`${heading.key}.${heading.subKey}`);
    } else if (heading.keys) {
      heading.keys.forEach((headingKey) => {
        if (headingKey.key && !headingKey.subKey) {
          keys.push(headingKey.key);
        } else if (headingKey.key && headingKey.subKey) {
          keys.push(`${headingKey.key}.${headingKey.subKey}`);
        }
      });
    }
  }

  const searcher = new FuzzySearch(data, keys, {
    caseSensitive: false,
  });
  const result = searcher.search(searchTerm);

  return result;
}

function renderAdditionalButtons({ additionalButtons }) {
  return additionalButtons.map((additionalButton) => {
    return (
      <div key={`button-${additionalButton.title}`}>
        <Button
          title={additionalButton.title}
          onClick={(payload) => additionalButton.onPress(payload)}
        />
      </div>
    );
  });
}

function transformData({ data, headings }) {
  return data.map((dataItem) => {
    let transformedDataItem = {
      data: dataItem,
    };

    headings.forEach((heading, i) => {
      let value = "";

      if (heading.hidden && heading.hidden(dataItem)) {
        return (transformedDataItem[heading.key] = value);
      }

      if (heading.keys) {
        heading.keys.forEach((headingKey) => {
          transformedDataItem[headingKey.key] = dataItem[headingKey.key];
        });
        return true;
      } else if (typeof heading.key === "string") {
        if (
          heading.type === "currency" &&
          dataItem[heading.key] !== null &&
          dataItem[heading.key] !== undefined
        ) {
          value = `£${dataItem[heading.key].toFixed(2)}`;
        } else if (heading.type === "yesNo") {
          if (dataItem[heading.key]) {
            value = "Yes";
          } else {
            value = "No";
          }
        } else if (heading.type === "date") {
          value = format(new Date(dataItem[heading.key]), "do MMMM yyyy");
        } else if (heading.type === "function" && heading.formatValue) {
          value = heading.formatValue(dataItem);
        } else {
          value = dataItem[heading.key];
        }
      } else {
        value = dataItem[heading.key];
      }

      return (transformedDataItem[heading.key] = value);
    });

    return transformedDataItem;
  });
}

function renderTableRows({
  classes,
  headings,
  data,
  hideControls,
  rowActions,
  searchTerm,
  onRowClick,
}) {
  if (data) {
    let transformedData = transformData({ data, headings });
    return filterData({ searchTerm, data: transformedData, headings }).map(
      (dataItem, i) => {
        return (
          <TableRow key={"row-" + i}>
            {headings.map((heading, i) => {
              let value = null;

              if (heading.keys) {
                console.log(
                  heading.keys,
                  dataItem,
                  heading.keys
                    .map((headingKey) => {
                      if (typeof dataItem[headingKey.key] === "object") {
                        if (headingKey.subKey) {
                          return dataItem[headingKey.key][headingKey.subKey];
                        }
                      }
                      return dataItem[headingKey.key];
                    })
                    .join(" ")
                );
                return (
                  <TableCell
                    onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                    key={"cell-" + i}
                    className={classes.tableCell}
                  >
                    {heading.keys
                      .map((headingKey) => {
                        if (typeof dataItem[headingKey.key] === "object") {
                          if (headingKey.subKey) {
                            return dataItem[headingKey.key][headingKey.subKey];
                          }
                        }
                        return dataItem[headingKey.key];
                      })
                      .join(" ")}
                    {heading.getTag(dataItem.data) && (
                      <span className={classes.fieldTag}>
                        {heading.getTag(dataItem.data)}
                      </span>
                    )}
                  </TableCell>
                );
              } else if (typeof heading.key === "string") {
                if (heading.type === "yesNo" || heading.type === "function") {
                  if (
                    dataItem[heading.key] === "Yes" ||
                    dataItem[heading.key] === "Submitted"
                  ) {
                    return (
                      <TableCell
                        onClick={() =>
                          onRowClick && onRowClick(dataItem.data.id)
                        }
                        key={"cell-" + i}
                        className={classes.tableCell}
                      >
                        <span className={classes.yesValue}>
                          {dataItem[heading.key]}
                        </span>
                      </TableCell>
                    );
                  } else if (dataItem[heading.key] === "No") {
                    return (
                      <TableCell
                        onClick={() =>
                          onRowClick && onRowClick(dataItem.data.id)
                        }
                        key={"cell-" + i}
                        className={classes.tableCell}
                      >
                        <span className={classes.noValue}>
                          {dataItem[heading.key]}
                        </span>
                      </TableCell>
                    );
                  }
                } else {
                  value = dataItem[heading.key];
                }
              } else if (heading.key.constructor === Array) {
                value = dataItem[heading.key[0]];
                for (let i = 1; i < heading.key.length; i++) {
                  if (value) {
                    value = value[heading.key[i]];
                  } else {
                    return <td key={"cell-" + i} />;
                  }
                }
              }

              if (!value) {
                return (
                  <TableCell
                    onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                    key={"cell-" + i}
                    className={classes.tableCell}
                  ></TableCell>
                );
              }

              if (typeof dataItem[heading.key] === "object") {
                if (heading.subKey) {
                  return (
                    <TableCell
                      onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                      key={"cell-" + i}
                      className={classes.tableCell}
                    >
                      {dataItem[heading.key][heading.subKey]}
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell
                      onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                      key={"cell-" + i}
                      className={classes.tableCell}
                    >
                      {JSON.stringify(value)}
                    </TableCell>
                  );
                }
              }
              return (
                <TableCell
                  onClick={() => onRowClick && onRowClick(dataItem.data.id)}
                  key={"cell-" + i}
                  className={classes.tableCell}
                >
                  {value.toString()}
                </TableCell>
              );
            })}

            {!hideControls && (
              <TableCell className={classes.tableCell} align="right">
                <div className={classes.tableActionsCell}>
                  {renderTableActions({ rowIndex: i, dataItem, rowActions })}
                </div>
              </TableCell>
            )}
          </TableRow>
        );
      }
    );
  }
  return false;
}

function DataTable({
  classes,
  headings,
  data,
  onRowClick,
  addButtonText,
  onAddButtonClick,
  rowLink,
  hideControls,
  hideSearch,
  rowActions,
  additionalButtons,
  showDateRangeFilter,
  dateRangeFilterStart,
  onDateRangeFilterStartChange,
  dateRangeFilterEnd,
  onDateRangeFilterEndChange,
  filterSelects,
  showDownloadButton,
  downloadFilename,
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [csvData, setCSVData] = React.useState([]);

  const generateCSV = useCallback(() => {
    let buildCSVData = [];

    buildCSVData.push(headings.map((heading) => heading.label));

    let transformedData = transformData({ data, headings });
    transformedData.forEach((dataItem) => {
      buildCSVData.push(
        headings.map((heading) => {
          return dataItem[heading.key];
        })
      );
    });

    setCSVData(buildCSVData);
  }, [data, headings]);

  useEffect(() => {
    if (showDownloadButton) {
      generateCSV();
    }
  }, [data, generateCSV, showDownloadButton]);

  return (
    <div>
      <div className={classes.tableHeader}>
        <div>
          {!hideSearch && !hideControls && (
            <SingleLineTextInput
              placeholder="Search..."
              style={{ marginRight: 0, marginBottom: 0 }}
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
          )}
          {/* <IconButton /> */}
        </div>
        <div className={classes.buttons}>
          <div>
            {addButtonText && !hideControls && onAddButtonClick && (
              <Button
                title={addButtonText}
                onClick={(payload) => onAddButtonClick(payload)}
              />
            )}
          </div>
          {showDownloadButton && (
            <CSVLink filename={`${downloadFilename}.csv`} data={csvData}>
              <Button title="Download report" />
            </CSVLink>
          )}
          {additionalButtons && renderAdditionalButtons({ additionalButtons })}
        </div>
      </div>
      {(showDateRangeFilter || (filterSelects && filterSelects.length > 0)) && (
        <div className={classes.filterSection}>
          {showDateRangeFilter && (
            <div>
              <Label title={"Start date"} />
              <DatePicker
                value={dateRangeFilterStart}
                onChange={(value) => onDateRangeFilterStartChange(value)}
                style={{ marginRight: 10 }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </div>
          )}
          {showDateRangeFilter && (
            <div>
              <Label title={"End date"} />
              <DatePicker
                value={dateRangeFilterEnd}
                onChange={(value) => onDateRangeFilterEndChange(value)}
                style={{ marginRight: 10 }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </div>
          )}
          {filterSelects &&
            filterSelects.map((filterSelect) => (
              <div className={classes.filterSelect}>
                <DropdownSelect
                  label={filterSelect.label}
                  value={filterSelect.value}
                  items={filterSelect.items}
                  onSelect={(value) => filterSelect.onSelect(value)}
                  placeholder={filterSelect.placeholder}
                />
              </div>
            ))}
        </div>
      )}
      <div className={classes.tableWrapper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {renderTableHeadings({ classes, headings })}
              <TableCell className={classes.tableCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableRows({
              classes,
              headings,
              data,
              rowLink,
              hideControls,
              rowActions,
              searchTerm,
              onRowClick,
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default withStyles(styles)(DataTable);
